
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";
import {
	decodeCrosswordClue,
	encodeCrosswordClue,
	getCrosswordPublisherByCrosswordId,
} from "@utils";
import {
	AD_LAYOUT_STUBS,
	PRODUCTION_ORIGIN,
	CROSSWORD_PUBLISHER_DATA,
} from "@consts";
import scrollTriggeredModalMixin from "@/plugins/mixins/scroll-triggered-modal";

dayjs.extend(utc);
dayjs.extend(tz);

// Shared data fetching function
const fetchClueData = async ({ axios, config, params, store }) => {
	// Check if there is a clue param first.
	const clueSlug = params?.clue;
	if (!clueSlug) {
		throw new Error("No clue provided");
	}

	// Decode the clue from the URL and fetch the first clue.
	const query = decodeCrosswordClue(clueSlug);
	const { data } = await axios.get(
		`${config.CWH_API_SERVICE}/crossword/v1/answers`,
		{
			responseType: "json",
			params: {
				query,
				max: 150,
				offset: 0,
			},
		}
	);

	// If clue was not found, throw an error.
	if (!data?.first?.clue) {
		throw new Error("Clue not found");
	}

	// If the clue is too old, throw an error.
	const OLDEST_ALLOWED_DATE = "2022-01-01";
	if (dayjs(data.first.date).isBefore(OLDEST_ALLOWED_DATE)) {
		throw new Error("This clue is too old.");
	}

	// Check if the clue matches the URL. If the case does not match, redirect to the correct URL.
	const decodedClue = decodeCrosswordClue(params.clue);
	if (data.first?.clue && data.first.clue !== decodedClue) {
		return {
			redirect: true,
			encodedClue: encodeCrosswordClue(data.first.clue),
		};
	}

	const publisherData = getCrosswordPublisherByCrosswordId(
		data.first.crosswordId
	);

	// Fetch other clues for the same crossword and date as the first clue (if publisher is one of the approved ones)
	const cluesResponse = await axios.get(
		`${config.CWH_API_SERVICE}/daily-crosswords/v1/crossword`,
		{
			responseType: "json",
			params: {
				crosswordId: data.first.crosswordId,
				date: data.first.date,
			},
		}
	);

	const { answers: cluesAnswers } = cluesResponse.data.crossword || {};
	const { across, down } = cluesAnswers || {};

	const cluesData = {
		across,
		down,
	};

	const breadcrumbs = [
		{ label: "CrosswordSolverBreadcrumb", url: "/crosswords/" },
		{
			label: "Clue",
		},
	];

	if (publisherData) {
		breadcrumbs.splice(1, 0, {
			label: publisherData.breadcrumbs,
			url: `/crosswords/${publisherData.slug}/`,
		});
	}

	store.commit("navigation/setBreadcrumbsOverride", breadcrumbs);

	const answers = data.answers.map((a) => ({
		answer: a.answer,
		score: Math.floor(a.score),
		isExactMatch: Math.floor(a.score) === 100,
	}));

	store.commit("clues/SET_FILTERED_ANSWERS", answers);
	store.commit("clues/SET_ALL_ANSWERS", answers);

	return {
		redirect: false,
		data: {
			clueData: data.first,
			cluesData,
			publisher: publisherData,
		},
	};
};

export default {
	name: "CrosswordClue",
	mixins: [scrollTriggeredModalMixin],
	async asyncData({ res, $axios, $config, error, params, store, redirect }) {
		try {
			const result = await fetchClueData({
				axios: $axios,
				config: $config,
				params,
				store,
			});

			if (result.redirect && process.server && res) {
				redirect(301, `/crosswords/clues/${result.encodedClue}/`);
				return;
			}

			// Set cache expiration header:
			if (process.server && res) {
				const now = dayjs().tz("America/New_York");
				const expirationDate = now
					.add(1, "year")
					.utc()
					.format("ddd, DD MMM YYYY HH:mm:ss [GMT]");

				res.setHeader("Expires", expirationDate);
			}

			return {
				serverDate: dayjs().format("YYYY-MM-DD"),
				loading: false,
				...result.data,
			};
		} catch (e) {
			error({
				statusCode: 404,
				message: e.message,
			});
		}
	},
	data() {
		return {
			AD_LAYOUT_STUBS,
			loading: false,
			clueData: null,
			cluesData: null,
		};
	},
	async fetch() {
		// Only fetch on client-side when we don't have data
		if (!process.server && (!this.clueData || !this.answers.length)) {
			try {
				const result = await fetchClueData({
					axios: this.$axios,
					config: this.$config,
					params: this.$route.params,
					store: this.$store,
				});

				if (result.redirect) {
					this.$router.replace(
						`/crosswords/clues/${result.encodedClue}/`
					);
					return;
				}

				// Update the data
				Object.assign(this, result.data);
			} catch (e) {
				console.error("Error in fetch: ", e);
				this.$nuxt.error({
					statusCode: 404,
					message: e.message,
				});
			}
		}
	},
	head({ $buildMetaDataObject }) {
		const meta = $buildMetaDataObject({
			title: `${this.clueText} | NYT Crossword Clue Answers`,
			description: `Find all the answers for today's New York Times crossword, including the answers to the "${this.clueText}" Crossword Clue.`,
		});

		const clueText = this.clueData?.clue;

		if (clueText) {
			return {
				...meta,
				link: [
					{
						rel: "canonical",
						href: `${PRODUCTION_ORIGIN}/crosswords/clues/${encodeCrosswordClue(
							clueText
						)}/`,
					},
				],
			};
		}

		return meta;
	},
	computed: {
		otherCluesPublisherData() {
			const iconPath =
				this.publisher?.iconPath ||
				CROSSWORD_PUBLISHER_DATA[0].iconPath;
			const shortTitle =
				this.publisher?.shortTitle ||
				`${this.clueData?.publisherId} - ${dayjs(
					this.clueData?.date
				).format("MMMM D")}`;

			return {
				iconPath,
				shortTitle,
			};
		},
		clueText() {
			try {
				return decodeCrosswordClue(this.$route.params.clue);
			} catch (e) {
				return "";
			}
		},
		lastSeenText() {
			try {
				const { source, date } = this.clueData || {};

				const dateObject = dayjs(date);
				if (!dateObject.isValid()) {
					throw new Error("Invalid last seen date");
				}
				const formattedDate = dateObject.format("D MMM YYYY");

				return `${source}, ${formattedDate}`;
			} catch (e) {
				return "";
			}
		},
		answerCountText() {
			const count = this.answers?.length || 0;
			if (!count) {
				return "";
			}

			if (count === 1) {
				return "Matching Answer";
			}

			return `Showing ${count} Matching Answers`;
		},
		// This ensures that the directions are always in the correct order.
		clueGroups() {
			const groups = [];

			["across", "down"].forEach((direction) => {
				if (!this.cluesData?.[direction]?.length) {
					return;
				}

				const pathBase = `/crosswords/clues`;
				groups.push({
					direction,
					clues: (this.cluesData?.[direction] || [])
						.filter((clue) => {
							return clue.clue !== this.clueData.clue;
						})
						.map((clue) => ({
							...clue,
							path: `${pathBase}/${encodeCrosswordClue(
								clue.clue
							)}`,
						})),
				});
			});

			return groups;
		},
		answers() {
			return this.$store.state.clues.filteredAnswers;
		},
	},
	methods: {
		formatDate(format, date) {
			const d = date ? dayjs(date) : dayjs();
			return d.format(format);
		},
	},
};
